import { z } from 'zod';
export const TariffTypeSchema = z.enum(['flat', 'time_of_use', 'tiered']);
/**
 * All rates are stored in cents (or cents-per-unit). A rate of 30 means
 * $0.30/kWh. Fractional cents are allowed for precision (e.g. 8.75 for
 * $0.0875/kWh). Totals are always rounded to integer cents.
 */
export const TouScheduleEntrySchema = z.object({
    day_of_week: z.array(z.number().int().min(0).max(6)),
    start_hour: z.number().int().min(0).max(23),
    end_hour: z.number().int().min(1).max(24),
    energy_rate: z.number().nonnegative(), // cents per kWh
});
/**
 * Tier entry for a Tiered tariff. The `tier_max` field is the
 * upper bound of the tier's coverage window — null = "and above"
 * (the last tier in the array is typically null).
 *
 * Field-rename history: this used to be `up_to_kwh` (Commit D
 * extended the dispatch so the tier's usage metric can be hours or
 * kWh depending on the tariff's rate_type; the kwh-suffixed name
 * became misleading). The schema accepts BOTH names during a
 * transition window:
 *   - New writes use `tier_max`
 *   - Reads prefer `tier_max`, fall back to `up_to_kwh`
 *   - A migration script (scripts/migrate-tier-max.ts) copies
 *     up_to_kwh → tier_max on every existing tiered tariff doc.
 *     Founder runs it once; the legacy field stays in place for
 *     one release before being removed in a follow-up commit.
 *
 * Mongoose strict-mode + the calculator's null-coalesce keep
 * mixed-state docs (one field set, the other null) reading
 * correctly throughout the transition.
 */
export const TieredRateEntrySchema = z.object({
    /** Upper bound of this tier — kWh for per_kwh tariffs, hours for
     *  per_hour tariffs. Null = "and above". */
    tier_max: z.number().positive().nullable().optional(),
    /** Legacy alias — same semantic as `tier_max`. Read-fallback
     *  only; new writes should use `tier_max`. Will be removed in
     *  a follow-up commit once the migration script has run. */
    up_to_kwh: z.number().positive().nullable().optional(),
    energy_rate: z.number().nonnegative(),
});
/** Resolve a tier's upper bound, preferring the new field name
 *  with a fallback to the legacy alias. Shared between billing
 *  math and admin form readers so the migration window stays
 *  consistent across every consumer. */
export function tierMax(t) {
    if (t.tier_max !== undefined)
        return t.tier_max;
    return t.up_to_kwh ?? null;
}
export const TariffRatesSchema = z.object({
    /** Base rate in cents/kWh — always present (used as fallback for TOU/tiered too) */
    energy_rate: z.number().nonnegative(),
    /** Idle/time-based fee in cents/minute */
    time_rate: z.number().nonnegative().optional(),
    /** The idle fee's grace, whole minutes after charging stops (5-30,
     *  required with an idle fee; Warren 2026-09-28). */
    idle_grace_minutes: z.number().int().min(5).max(30).nullable().optional(),
    idle_max_charge_cents: z.number().int().nonnegative().nullable().optional(),
    /** One-time fee charged at session start, in cents */
    flat_fee: z.number().nonnegative().optional(),
    /**
     * Expected typical session runtime in hours, used by the pay-as-you-go
     * pre-auth calculator. AC L2 at multifamily often runs overnight
     * (8–12h); DCFC sessions cap around 60–90 minutes. Per-tariff override
     * for the deployment context this tariff serves. When null, the
     * calculator falls back to WalletSettings.preAuth.maxSessionHours.
     */
    expected_session_hours: z.number().min(0.5).max(24).nullable().optional(),
    /** TOU schedule entries (only used when type='time_of_use') */
    time_of_use_schedule: z.array(TouScheduleEntrySchema).optional(),
    /** Tiered thresholds (only used when type='tiered') */
    tiered_schedule: z.array(TieredRateEntrySchema).optional(),
});
export const TariffSchema = z.object({
    _id: z.string(),
    tenant_id: z.string(),
    name: z.string().min(1),
    type: TariffTypeSchema,
    currency: z.string().length(3),
    rates: TariffRatesSchema,
    /** Tax rate as a decimal (0.0875 = 8.75%) */
    tax_rate: z.number().min(0).max(1),
    /**
     * Per-tariff unit selector — applies to all rates within the
     * tariff (flat, TOU windows, tiered tier rates). 'per_kwh' is the
     * historical default; 'per_hour' switches billing to
     * duration_hours × rate for the customer-facing leg. Per-window
     * rate_type within a TOU tariff is intentionally NOT supported —
     * tariff-level keeps it simple and matches operator mental
     * models. Use Location.electricity_tou_windows for per-window
     * unit control on the building-reimbursement side.
     *
     * Defaults to 'per_kwh' so legacy Tariff docs hydrate unchanged.
     * Uses the same enum as Location TOU rate_type (Commit B).
     */
    rate_type: z.enum(['per_kwh', 'per_hour']).default('per_kwh'),
    is_default: z.boolean(),
    is_active: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
export const CreateTariffSchema = TariffSchema.omit({
    _id: true, tenant_id: true, created_at: true, updated_at: true,
});
export const AssignTariffSchema = z.object({
    /** Target type — location or charger */
    target_type: z.enum(['location', 'charger']),
    target_id: z.string(),
});
//# sourceMappingURL=tariff.js.map